import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { Card } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Input } from '../components/ui/input';
import { Textarea } from '../components/ui/textarea';
import { Label } from '../components/ui/label';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import {
  ArrowLeft,
  Clock,
  User,
  Calendar,
  FileText,
  MessageSquare,
  Plus,
  CheckCircle,
  XCircle,
  AlertTriangle,
  Send,
  Briefcase,
  History,
  Edit,
  Loader2,
  ExternalLink,
  Users,
  Mail,
  Lock,
} from 'lucide-react';
import { toast } from 'sonner';
import api from '../utils/axios';

const ESTADOS = {
  PENDIENTE: { label: 'Pendiente', color: 'bg-yellow-100 text-yellow-800', icon: Clock },
  EN_PROCESO: { label: 'En Proceso', color: 'bg-blue-100 text-blue-800', icon: Loader2 },
  APROBADA: { label: 'Aprobada', color: 'bg-green-100 text-green-800', icon: CheckCircle },
  RECHAZADA: { label: 'Rechazada', color: 'bg-red-100 text-red-800', icon: XCircle },
  APLICADA: { label: 'Aplicada', color: 'bg-emerald-100 text-emerald-800', icon: CheckCircle },
  COMPLETADA: { label: 'Completada', color: 'bg-green-100 text-green-800', icon: CheckCircle },
};

const PRIORIDADES = {
  CRITICA: { label: 'Crítica', color: 'bg-red-100 text-red-800' },
  ALTA: { label: 'Alta', color: 'bg-orange-100 text-orange-800' },
  MEDIA: { label: 'Media', color: 'bg-yellow-100 text-yellow-800' },
  BAJA: { label: 'Baja', color: 'bg-green-100 text-green-800' },
};

const SolicitudDetalle = () => {
  const { solicitudId } = useParams();
  const navigate = useNavigate();
  
  const [solicitud, setSolicitud] = useState(null);
  const [loading, setLoading] = useState(true);
  const [newNote, setNewNote] = useState('');
  const [addingNote, setAddingNote] = useState(false);
  const [showActionModal, setShowActionModal] = useState(false);
  const [actionForm, setActionForm] = useState({
    accion: '',
    comentario: ''
  });
  const [submittingAction, setSubmittingAction] = useState(false);
  const [users, setUsers] = useState([]);
  const [grupos, setGrupos] = useState([]);

  // Cierre de solicitud con email
  const [showCierreDialog, setShowCierreDialog] = useState(false);
  const [resoluciones, setResoluciones] = useState([]);
  const [plantillas, setPlantillas] = useState([]);
  const [cierreForm, setCierreForm] = useState({
    resolucion_id: '', notas_cierre: '', enviar_email: true,
    email_destinatario: '', plantilla_id: '', email_asunto: '', email_contenido: ''
  });
  const [emailSugerido, setEmailSugerido] = useState(null);
  const [cerrando, setCerrando] = useState(false);

  useEffect(() => {
    loadSolicitud();
    loadUsers();
    loadGrupos();
  }, [solicitudId]);

  const loadSolicitud = async () => {
    setLoading(true);
    try {
      // Intentar cargar desde solicitudes primero
      let res = await api.get(`/solicitudes/${solicitudId}`);
      setSolicitud(res.data);
    } catch (error) {
      // Si no está en solicitudes, buscar en approvals
      try {
        const res = await api.get(`/approvals/${solicitudId}`);
        setSolicitud(res.data);
      } catch (err) {
        toast.error('Solicitud no encontrada');
        navigate(-1);
      }
    } finally {
      setLoading(false);
    }
  };

  const loadUsers = async () => {
    try {
      const res = await api.get('/users');
      setUsers(res.data || []);
    } catch (error) {
    }
  };

  const loadGrupos = async () => {
    try {
      const res = await api.get('/solicitudes/config/grupos');
      setGrupos(res.data || []);
    } catch (error) {
    }
  };

  const handleAddNote = async () => {
    if (!newNote.trim()) return;
    
    setAddingNote(true);
    try {
      await api.post(`/solicitudes/${solicitudId}/notas`, {
        texto: newNote
      });
      toast.success('Nota agregada');
      setNewNote('');
      loadSolicitud();
    } catch (error) {
      toast.error('Error al agregar nota');
    } finally {
      setAddingNote(false);
    }
  };

  const handleAction = async () => {
    if (!actionForm.accion) {
      toast.error('Seleccione una acción');
      return;
    }
    
    setSubmittingAction(true);
    try {
      await api.post(`/solicitudes/${solicitudId}/acciones`, {
        accion: actionForm.accion,
        comentario: actionForm.comentario
      });
      toast.success('Acción registrada correctamente');
      setShowActionModal(false);
      setActionForm({ accion: '', comentario: '' });
      loadSolicitud();
    } catch (error) {
      toast.error('Error al registrar acción');
    } finally {
      setSubmittingAction(false);
    }
  };

  const handleChangeEstado = async (nuevoEstado) => {
    try {
      await api.patch(`/solicitudes/${solicitudId}/estado`, {
        estado: nuevoEstado
      });
      toast.success('Estado actualizado');
      loadSolicitud();
    } catch (error) {
      toast.error('Error al actualizar estado');
    }
  };

  // ===== Cierre de Solicitud con Email =====
  const openCierreDialog = async () => {
    try {
      const [resRes, pltRes, emailRes] = await Promise.all([
        api.get('/solicitudes-config/resoluciones'),
        api.get('/solicitudes-config/plantillas'),
        api.get(`/solicitudes/${solicitudId}/email-sugerido`).catch(() => ({ data: {} }))
      ]);
      setResoluciones(resRes.data || []);
      setPlantillas(pltRes.data || []);
      setEmailSugerido(emailRes.data);
      setCierreForm({
        resolucion_id: '', notas_cierre: '', enviar_email: true,
        email_destinatario: emailRes.data?.email_sugerido || '',
        plantilla_id: '', email_asunto: '', email_contenido: ''
      });
      setShowCierreDialog(true);
    } catch (error) {
      toast.error('Error al cargar configuración de cierre');
    }
  };

  const procesarPlantilla = (texto) => {
    if (!texto || !solicitud) return '';
    return texto
      .replace(/\{\{numero_referencia\}\}/g, solicitud.numero_referencia || '')
      .replace(/\{\{cliente_nombre\}\}/g, emailSugerido?.cliente_nombre || solicitud.entidad_nombre || '')
      .replace(/\{\{tipo_solicitud\}\}/g, solicitud.tipo_solicitud || '')
      .replace(/\{\{descripcion\}\}/g, solicitud.descripcion || '');
  };

  const handleResolucionChange = (resolucionId) => {
    const resolucion = resoluciones.find(r => r.id === resolucionId);
    let newForm = { ...cierreForm, resolucion_id: resolucionId };
    if (resolucion?.plantilla_sugerida_id) {
      const plantilla = plantillas.find(p => p.id === resolucion.plantilla_sugerida_id);
      if (plantilla) {
        newForm.plantilla_id = plantilla.id;
        newForm.email_asunto = procesarPlantilla(plantilla.asunto);
        newForm.email_contenido = procesarPlantilla(plantilla.contenido);
      }
    }
    newForm.enviar_email = !!resolucion?.requiere_email;
    setCierreForm(newForm);
  };

  const handlePlantillaChange = (plantillaId) => {
    const plantilla = plantillas.find(p => p.id === plantillaId);
    if (plantilla) {
      setCierreForm({
        ...cierreForm, plantilla_id: plantillaId,
        email_asunto: procesarPlantilla(plantilla.asunto),
        email_contenido: procesarPlantilla(plantilla.contenido)
      });
    }
  };

  const handleCerrarSolicitud = async () => {
    if (!cierreForm.resolucion_id) { toast.error('Debe seleccionar una resolución'); return; }
    if (cierreForm.enviar_email && !cierreForm.email_destinatario) { toast.error('Debe ingresar un email destinatario'); return; }
    setCerrando(true);
    try {
      await api.post(`/solicitudes/${solicitudId}/cerrar`, cierreForm);
      toast.success('Solicitud cerrada correctamente');
      setShowCierreDialog(false);
      loadSolicitud();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Error al cerrar solicitud');
    } finally {
      setCerrando(false);
    }
  };

  const getResponsableNombre = () => {
    if (solicitud?.responsable_id) {
      const user = users.find(u => u.id === solicitud.responsable_id);
      return user ? user.nombre : 'Usuario asignado';
    }
    if (solicitud?.grupo_asignado_id) {
      const grupo = grupos.find(g => g.id === solicitud.grupo_asignado_id);
      return grupo ? `Grupo: ${grupo.nombre}` : 'Grupo asignado';
    }
    return 'Sin asignar';
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-96">
        <Loader2 className="w-8 h-8 animate-spin text-blue-500" />
      </div>
    );
  }

  if (!solicitud) {
    return (
      <div className="flex flex-col items-center justify-center h-96 text-slate-500">
        <AlertTriangle size={48} className="mb-4" />
        <p>Solicitud no encontrada</p>
        <Button onClick={() => navigate(-1)} className="mt-4">
          Volver
        </Button>
      </div>
    );
  }

  const estadoInfo = ESTADOS[solicitud.estado] || ESTADOS.PENDIENTE;
  const prioridadInfo = PRIORIDADES[solicitud.prioridad] || PRIORIDADES.MEDIA;
  const EstadoIcon = estadoInfo.icon;

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-4">
          <Button
            variant="ghost"
            size="sm"
            onClick={() => navigate(-1)}
            className="text-slate-600"
          >
            <ArrowLeft size={18} className="mr-1" />
            Volver
          </Button>
          <div>
            <h1 className="text-2xl font-bold text-slate-900 flex items-center gap-3">
              <FileText className="text-blue-600" />
              {solicitud.tipo_solicitud || solicitud.numero_referencia || 'Solicitud'}
            </h1>
            <p className="text-sm text-slate-500 mt-1">
              {solicitud.numero_referencia && `Ref: ${solicitud.numero_referencia} · `}
              Creada el {new Date(solicitud.created_at || solicitud.fecha_creacion).toLocaleDateString()}
            </p>
          </div>
        </div>
        
        <div className="flex items-center gap-3">
          {solicitud.estado !== 'CERRADA' && solicitud.estado !== 'COMPLETADA' && (
            <Button
              onClick={openCierreDialog}
              className="bg-green-600 hover:bg-green-700 text-white"
              data-testid="cerrar-solicitud-btn"
            >
              <Lock size={16} className="mr-2" />
              Cerrar Solicitud
            </Button>
          )}
          <Badge className={prioridadInfo.color}>
            {prioridadInfo.label}
          </Badge>
          <Badge className={`${estadoInfo.color} flex items-center gap-1`}>
            <EstadoIcon size={14} />
            {estadoInfo.label}
          </Badge>
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Columna Principal */}
        <div className="lg:col-span-2 space-y-6">
          {/* Información Principal */}
          <Card className="p-6 bg-white">
            <h3 className="font-semibold text-lg mb-4 flex items-center gap-2">
              <Briefcase size={20} className="text-blue-600" />
              Información de la Solicitud
            </h3>
            
            <div className="space-y-4">
              <div>
                <Label className="text-xs text-slate-500">Descripción / Motivo</Label>
                <p className="text-slate-800 mt-1">{solicitud.descripcion || 'Sin descripción'}</p>
              </div>
              
              {solicitud.entidad_tipo && (
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <Label className="text-xs text-slate-500">Tipo de Entidad</Label>
                    <p className="text-slate-800 mt-1">{solicitud.entidad_tipo}</p>
                  </div>
                  <div>
                    <Label className="text-xs text-slate-500">Entidad</Label>
                    <p className="text-slate-800 mt-1">
                      {solicitud.entidad_nombre || (
                        solicitud.entidad_id ? (
                          // CVU tiene 22 dígitos, CUIT tiene 11. Priorizar CVU ya que es el identificador principal
                          `CVU: ${solicitud.entidad_id}`
                        ) : '-'
                      )}
                    </p>
                  </div>
                </div>
              )}
              
              {solicitud.campos_adicionales?.caso_id && (
                <div className="p-3 bg-blue-50 rounded-lg border border-blue-200">
                  <Label className="text-xs text-blue-600">Caso Vinculado</Label>
                  <div className="flex items-center justify-between mt-1">
                    <p className="text-blue-800 font-medium">
                      {solicitud.campos_adicionales.caso_titulo || 'Ver caso'}
                    </p>
                    <Button
                      size="sm"
                      variant="outline"
                      onClick={() => navigate(`/m/gestion-de-casos/caso/${solicitud.campos_adicionales.caso_id}`)}
                      className="text-blue-600"
                    >
                      <ExternalLink size={14} className="mr-1" />
                      Ir al Caso
                    </Button>
                  </div>
                </div>
              )}
            </div>
          </Card>

          {/* Historial de Acciones */}
          <Card className="p-6 bg-white">
            <div className="flex items-center justify-between mb-4">
              <h3 className="font-semibold text-lg flex items-center gap-2">
                <History size={20} className="text-purple-600" />
                Historial de Acciones
              </h3>
              <Button
                size="sm"
                onClick={() => setShowActionModal(true)}
                className="bg-purple-600 hover:bg-purple-700 text-white"
              >
                <Plus size={14} className="mr-1" />
                Registrar Acción
              </Button>
            </div>
            
            <div className="space-y-3 max-h-64 overflow-y-auto">
              {solicitud.historial?.length > 0 ? (
                solicitud.historial.map((item, idx) => (
                  <div key={idx} className="flex gap-3 p-3 bg-slate-50 rounded-lg">
                    <div className={`w-8 h-8 rounded-full flex items-center justify-center flex-shrink-0 ${
                      item.accion === 'APROBADA' || item.accion === 'COMPLETADA' ? 'bg-green-100 text-green-600' :
                      item.accion === 'RECHAZADA' ? 'bg-red-100 text-red-600' :
                      'bg-blue-100 text-blue-600'
                    }`}>
                      {item.accion === 'APROBADA' || item.accion === 'COMPLETADA' ? <CheckCircle size={16} /> :
                       item.accion === 'RECHAZADA' ? <XCircle size={16} /> :
                       <Edit size={16} />}
                    </div>
                    <div className="flex-1">
                      <p className="font-medium text-sm text-slate-800">{item.accion}</p>
                      {item.comentario && (
                        <p className="text-sm text-slate-600 mt-0.5">{item.comentario}</p>
                      )}
                      <p className="text-xs text-slate-400 mt-1">
                        {item.usuario} · {new Date(item.fecha).toLocaleString()}
                      </p>
                    </div>
                  </div>
                ))
              ) : (
                <p className="text-sm text-slate-400 text-center py-4">Sin acciones registradas</p>
              )}
            </div>
          </Card>

          {/* Notas */}
          <Card className="p-6 bg-white">
            <h3 className="font-semibold text-lg mb-4 flex items-center gap-2">
              <MessageSquare size={20} className="text-pink-600" />
              Notas
            </h3>
            
            <div className="space-y-3 mb-4 max-h-48 overflow-y-auto">
              {solicitud.notas?.length > 0 ? (
                solicitud.notas.map((nota, idx) => (
                  <div key={idx} className="p-3 bg-pink-50 rounded-lg border border-pink-100">
                    <p className="text-sm text-slate-800">{nota.texto}</p>
                    <p className="text-xs text-slate-500 mt-1">
                      {nota.usuario} · {new Date(nota.fecha).toLocaleString()}
                    </p>
                  </div>
                ))
              ) : (
                <p className="text-sm text-slate-400 text-center py-4">Sin notas</p>
              )}
            </div>
            
            <div className="flex gap-2">
              <Input
                value={newNote}
                onChange={(e) => setNewNote(e.target.value)}
                placeholder="Agregar una nota..."
                onKeyPress={(e) => e.key === 'Enter' && handleAddNote()}
              />
              <Button
                onClick={handleAddNote}
                disabled={addingNote || !newNote.trim()}
                className="bg-pink-600 hover:bg-pink-700 text-white"
              >
                {addingNote ? <Loader2 size={16} className="animate-spin" /> : <Send size={16} />}
              </Button>
            </div>
          </Card>
        </div>

        {/* Columna Lateral */}
        <div className="space-y-6">
          {/* Cambiar Estado */}
          <Card className="p-4 bg-white">
            <h4 className="font-semibold mb-3 flex items-center gap-2">
              <Edit size={16} className="text-slate-600" />
              Cambiar Estado
            </h4>
            <Select
              value={solicitud.estado}
              onValueChange={handleChangeEstado}
            >
              <SelectTrigger>
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {Object.entries(ESTADOS).map(([key, val]) => (
                  <SelectItem key={key} value={key}>{val.label}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </Card>

          {/* Información de Asignación */}
          <Card className="p-4 bg-white">
            <h4 className="font-semibold mb-3 flex items-center gap-2">
              <Users size={16} className="text-slate-600" />
              Asignación
            </h4>
            <div className="space-y-3 text-sm">
              <div>
                <Label className="text-xs text-slate-500">Responsable</Label>
                <p className="text-slate-800">{getResponsableNombre()}</p>
              </div>
              <div>
                <Label className="text-xs text-slate-500">Solicitante</Label>
                <p className="text-slate-800">
                  {solicitud.solicitante?.nombre || solicitud.solicitante_email || 'N/A'}
                </p>
              </div>
            </div>
          </Card>

          {/* Fechas */}
          <Card className="p-4 bg-white">
            <h4 className="font-semibold mb-3 flex items-center gap-2">
              <Calendar size={16} className="text-slate-600" />
              Fechas
            </h4>
            <div className="space-y-3 text-sm">
              <div>
                <Label className="text-xs text-slate-500">Fecha Creación</Label>
                <p className="text-slate-800">
                  {new Date(solicitud.created_at || solicitud.fecha_creacion).toLocaleString()}
                </p>
              </div>
              {solicitud.fecha_limite && (
                <div>
                  <Label className="text-xs text-slate-500">Fecha Límite</Label>
                  <p className="text-slate-800">
                    {new Date(solicitud.fecha_limite).toLocaleString()}
                  </p>
                </div>
              )}
              <div>
                <Label className="text-xs text-slate-500">Última Actualización</Label>
                <p className="text-slate-800">
                  {new Date(solicitud.updated_at || solicitud.fecha_actualizacion).toLocaleString()}
                </p>
              </div>
            </div>
          </Card>

          {/* Datos Adicionales */}
          {solicitud.campos_adicionales && Object.keys(solicitud.campos_adicionales).length > 0 && (
            <Card className="p-4 bg-white">
              <h4 className="font-semibold mb-3">Datos Adicionales</h4>
              <div className="space-y-2 text-sm">
                {Object.entries(solicitud.campos_adicionales)
                  .filter(([key]) => !['caso_id', 'caso_titulo'].includes(key))
                  .map(([key, value]) => (
                    <div key={key}>
                      <Label className="text-xs text-slate-500">{key.replace(/_/g, ' ')}</Label>
                      <p className="text-slate-800">{String(value)}</p>
                    </div>
                  ))}
              </div>
            </Card>
          )}
        </div>
      </div>

      {/* Modal de Acción */}
      {showActionModal && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
          <Card className="w-full max-w-md p-6 bg-white m-4">
            <h3 className="text-lg font-semibold mb-4">Registrar Acción</h3>
            
            <div className="space-y-4">
              <div>
                <Label>Tipo de Acción</Label>
                <Select
                  value={actionForm.accion}
                  onValueChange={(v) => setActionForm({ ...actionForm, accion: v })}
                >
                  <SelectTrigger className="mt-1">
                    <SelectValue placeholder="Seleccionar acción..." />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="EN_REVISION">Tomar en Revisión</SelectItem>
                    <SelectItem value="SOLICITAR_INFO">Solicitar Información</SelectItem>
                    <SelectItem value="ESCALAR">Escalar</SelectItem>
                    <SelectItem value="APROBAR">Aprobar</SelectItem>
                    <SelectItem value="RECHAZAR">Rechazar</SelectItem>
                    <SelectItem value="COMPLETAR">Completar</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              
              <div>
                <Label>Comentario</Label>
                <Textarea
                  value={actionForm.comentario}
                  onChange={(e) => setActionForm({ ...actionForm, comentario: e.target.value })}
                  placeholder="Agregar comentario sobre la acción..."
                  className="mt-1"
                  rows={3}
                />
              </div>
            </div>
            
            <div className="flex justify-end gap-2 mt-6">
              <Button variant="outline" onClick={() => setShowActionModal(false)}>Cancelar</Button>
              <Button
                onClick={handleAction}
                disabled={submittingAction || !actionForm.accion}
                className="bg-purple-600 hover:bg-purple-700 text-white"
              >
                {submittingAction ? <Loader2 size={16} className="animate-spin mr-1" /> : <CheckCircle size={16} className="mr-1" />}
                Registrar
              </Button>
            </div>
          </Card>
        </div>
      )}

      {/* Modal de Cierre de Solicitud */}
      {showCierreDialog && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
          <Card className="w-full max-w-2xl p-6 bg-white m-4 max-h-[90vh] overflow-y-auto">
            <h3 className="text-lg font-semibold mb-1 flex items-center gap-2">
              <Lock size={20} className="text-green-600" />
              Cerrar Solicitud
            </h3>
            <p className="text-sm text-slate-500 mb-4">
              {solicitud.numero_referencia} - {solicitud.tipo_solicitud}
            </p>
            
            <div className="space-y-4">
              {/* Resolución */}
              <div>
                <Label className="font-medium">Resolución *</Label>
                <Select value={cierreForm.resolucion_id} onValueChange={handleResolucionChange}>
                  <SelectTrigger className="mt-1">
                    <SelectValue placeholder="Seleccionar resolución..." />
                  </SelectTrigger>
                  <SelectContent>
                    {resoluciones.filter(r => r.activo).map(r => (
                      <SelectItem key={r.id} value={r.id}>
                        <span className="flex items-center gap-2">
                          <span className="w-3 h-3 rounded-full" style={{ backgroundColor: r.color || '#6b7280' }} />
                          {r.nombre}
                        </span>
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                {cierreForm.resolucion_id && (
                  <p className="text-xs text-slate-500 mt-1">
                    {resoluciones.find(r => r.id === cierreForm.resolucion_id)?.descripcion}
                  </p>
                )}
              </div>

              {/* Notas de cierre */}
              <div>
                <Label className="font-medium">Notas de Cierre</Label>
                <Textarea
                  value={cierreForm.notas_cierre}
                  onChange={(e) => setCierreForm({ ...cierreForm, notas_cierre: e.target.value })}
                  placeholder="Notas adicionales sobre el cierre..."
                  className="mt-1" rows={2}
                />
              </div>

              {/* Email section */}
              <div className="border rounded-lg p-4 space-y-3 bg-slate-50">
                <div className="flex items-center gap-2">
                  <input
                    type="checkbox"
                    id="enviar_email_detalle"
                    checked={cierreForm.enviar_email}
                    onChange={(e) => setCierreForm({ ...cierreForm, enviar_email: e.target.checked })}
                    className="rounded"
                  />
                  <Label htmlFor="enviar_email_detalle" className="font-medium flex items-center gap-1 cursor-pointer">
                    <Mail size={16} className="text-blue-600" />
                    Enviar email al cliente
                  </Label>
                </div>

                {cierreForm.enviar_email && (
                  <div className="space-y-3 pl-6">
                    <div>
                      <Label className="text-sm">Email Destinatario *</Label>
                      <Input
                        value={cierreForm.email_destinatario}
                        onChange={(e) => setCierreForm({ ...cierreForm, email_destinatario: e.target.value })}
                        placeholder="email@ejemplo.com"
                        className="mt-1"
                      />
                    </div>

                    <div>
                      <Label className="text-sm">Plantilla de Respuesta</Label>
                      <Select value={cierreForm.plantilla_id} onValueChange={handlePlantillaChange}>
                        <SelectTrigger className="mt-1">
                          <SelectValue placeholder="Seleccionar plantilla..." />
                        </SelectTrigger>
                        <SelectContent>
                          {plantillas.filter(p => p.activo).map(p => (
                            <SelectItem key={p.id} value={p.id}>{p.nombre}</SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>

                    <div>
                      <Label className="text-sm">Asunto del Email</Label>
                      <Input
                        value={cierreForm.email_asunto}
                        onChange={(e) => setCierreForm({ ...cierreForm, email_asunto: e.target.value })}
                        placeholder="Asunto..."
                        className="mt-1"
                      />
                    </div>

                    <div>
                      <Label className="text-sm">Contenido del Email</Label>
                      <Textarea
                        value={cierreForm.email_contenido}
                        onChange={(e) => setCierreForm({ ...cierreForm, email_contenido: e.target.value })}
                        placeholder="Contenido del email..."
                        className="mt-1" rows={5}
                      />
                    </div>
                  </div>
                )}
              </div>
            </div>

            <div className="flex justify-end gap-2 mt-6">
              <Button variant="outline" onClick={() => setShowCierreDialog(false)}>Cancelar</Button>
              <Button
                onClick={handleCerrarSolicitud}
                disabled={cerrando || !cierreForm.resolucion_id}
                className="bg-green-600 hover:bg-green-700 text-white"
                data-testid="confirmar-cierre-btn"
              >
                {cerrando ? <Loader2 size={16} className="animate-spin mr-2" /> : <CheckCircle size={16} className="mr-2" />}
                Confirmar Cierre
              </Button>
            </div>
          </Card>
        </div>
      )}
    </div>
  );
};

export default SolicitudDetalle;
